import { useLocale } from '@multiplatform-apps/service-trips';
import React, { PropsWithChildren, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDeviceType } from '../hooks';
import { useModal } from './modal-context';
import { FeedbackAnnouncement, FeedbackModalWrapper } from './styles';
import { FeedbackContextType, FeedbackData } from './types';

const FeedbackContext = createContext<FeedbackContextType>(
  {} as FeedbackContextType
);

export const FeedbackProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [data, setData] = useState<FeedbackData>();
  const triggerRef = useRef<Element | null>(null);
  const deviceType = useDeviceType();

  const { isAnyModalOpen } = useModal();
  const { t } = useLocale();

  const show = useCallback((input: FeedbackData) => {
    triggerRef.current = document.activeElement;
    setData(input);
  }, []);

  const hide = useCallback(() => {
    setData(undefined);
  }, []);

  useEffect(() => {
    const isClosed = !data && !isAnyModalOpen;
    const trigger = triggerRef.current;
    if (isClosed && trigger instanceof HTMLElement) {
      trigger.focus();
      triggerRef.current = null;
    }
  }, [data, isAnyModalOpen]);

  const value = useMemo(() => ({ show, hide }), [show, hide]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <FeedbackAnnouncement data={data} />
      <FeedbackModalWrapper
        data={data}
        isAnyModalOpen={isAnyModalOpen}
        deviceType={deviceType}
        closeButtonLabel={t('common.close')}
      />
    </FeedbackContext.Provider>
  );
};

export const useFeedbackModal = () => React.useContext(FeedbackContext);
